import { reactive, computed } from 'vue';

// Module-level singleton so every message bubble, the floating action bar, and
// the conversation view share one selection without prop-drilling through the
// message list. Keyed by conversation so switching chats never carries a stale
// selection across.
//
// We store only message IDs (not payload snapshots): the actual content is
// derived live from the conversation's messages at forward time (see
// MessagesView.selectedMessages), so a message edited after it was ticked
// forwards its CURRENT content, and one that was deleted/revoked is excluded
// there rather than re-sent from a stale snapshot.
const state = reactive({
  active: false,
  conversationId: null,
  ids: new Set(),
});

const reset = () => {
  state.active = false;
  state.ids.clear();
};

export function useMessageSelection() {
  const isSelectMode = computed(() => state.active);
  const selectedCount = computed(() => state.ids.size);
  const selectedIds = computed(() => state.ids);

  const isSelected = id => state.ids.has(id);

  const enterSelectMode = conversationId => {
    if (state.conversationId !== conversationId) {
      state.ids.clear();
      state.conversationId = conversationId;
    }
    state.active = true;
  };

  // "Select messages" from the context menu — always adds, never toggles, so
  // re-invoking it on an already-selected message can't silently deselect it.
  const select = id => {
    state.ids.add(id);
  };

  const toggle = id => {
    if (state.ids.has(id)) {
      state.ids.delete(id);
    } else {
      state.ids.add(id);
    }
  };

  // Drop a set of ids (e.g. the ones that were successfully forwarded) while
  // keeping the rest selected for a retry.
  const deselect = ids => {
    ids.forEach(id => state.ids.delete(id));
  };

  const exitSelectMode = () => reset();

  // Called when the open conversation changes — drop any selection that belonged
  // to the previous conversation and leave select mode.
  const resetForConversation = conversationId => {
    if (state.conversationId !== conversationId) {
      state.conversationId = conversationId;
      reset();
    }
  };

  return {
    isSelectMode,
    selectedCount,
    selectedIds,
    isSelected,
    enterSelectMode,
    select,
    toggle,
    deselect,
    exitSelectMode,
    resetForConversation,
  };
}
